// ─── clientAppConfig.js ──────────────────────────────────────────────
// Lire/écrire la config par-cliente côté staging app cliente depuis le SaaS.
//
// Aujourd'hui : flags weight_tracking. Extensible pour d'autres options
// produit qu'Anissa peut activer par cliente (ex: visibilité plan, etc.).

// V96.35 : passe par le proxy server-side `/api/client-app-proxy`
import { clientAppFetch, ClientAppConfigError, ClientAppHttpError } from "./clientAppFetch";
// V97.40 (roadmap 1.2) : envoie email ET client_id (matching robuste hide-my-email)
import { clientIdentityFields, hasClientIdentity } from "./clientIdentity";

export class ClientConfigError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ClientConfigError";
    this.status = status;
  }
}

// Les messages viennent de l'app cliente, en anglais et avec son vocabulaire
// technique. Anissa ne doit lire ni « staging » ni de l'anglais : on traduit ce
// qu'on reconnaît, et on nettoie le reste plutôt que de le recracher tel quel.
const MESSAGES_CONNUS = Object.freeze([
  {
    motif: /not in staging|publish (her|the) plan|client not found|no client/i,
    texte: "L’espace cliente n’existe pas encore. Publie d’abord son programme, cette option sera activable ensuite.",
  },
  {
    motif: /unauthor|forbidden|invalid token|admin secret/i,
    texte: "L’accès à l’espace cliente a été refusé. Préviens Benoit, c’est un réglage à revoir.",
  },
  {
    motif: /timeout|network|fetch failed|econnrefused/i,
    texte: "L’espace cliente n’a pas répondu. Réessaie dans un instant.",
  },
]);

/** Traduit un message d'erreur technique en une phrase actionnable. */
export function messageConfigLisible(message) {
  const brut = String(message || "").trim();
  if (!brut) return "L’option n’a pas pu être lue. Réessaie dans un instant.";
  const connu = MESSAGES_CONNUS.find(({ motif }) => motif.test(brut));
  if (connu) return connu.texte;
  // Message inconnu : on ne prétend pas le comprendre, mais on ne laisse pas
  // passer le jargon interne ni une phrase entièrement en anglais.
  if (/staging/i.test(brut) || !/[àâçéèêëîïôûùüÿœ]/i.test(brut)) {
    return "L’option n’a pas pu être modifiée. Réessaie, et préviens Benoit si ça persiste.";
  }
  return brut;
}

function wrapErr(err) {
  if (err instanceof ClientAppConfigError) return new ClientConfigError(messageConfigLisible(err.message), 0);
  if (err instanceof ClientAppHttpError) return new ClientConfigError(messageConfigLisible(err.message), err.status);
  return err;
}

/** GET la config actuelle de la cliente (côté staging). */
export async function fetchClientAppConfig(client) {
  if (!hasClientIdentity(client)) throw new ClientConfigError("Cliente sans email ni client_id", 0);

  let body;
  try {
    body = await clientAppFetch("/api/admin/client-config", { method: "GET", query: clientIdentityFields(client) });
  } catch (e) { throw wrapErr(e); }
  if (!body?.ok) throw new ClientConfigError(messageConfigLisible(body?.error), 0);
  return {
    found: !!body.found,
    config: body.config || { weight_tracking_enabled: false, weight_visible_to_client: false },
  };
}

/** POST une mise à jour partielle de la config (1+ flags). */
export async function updateClientAppConfig(client, updates) {
  if (!hasClientIdentity(client)) throw new ClientConfigError("Cliente sans email ni client_id", 0);

  let body;
  try {
    body = await clientAppFetch("/api/admin/client-config", { method: "POST", payload: { ...clientIdentityFields(client), ...updates } });
  } catch (e) { throw wrapErr(e); }
  if (!body?.ok) throw new ClientConfigError(messageConfigLisible(body?.error), 0);
  return body.config;
}
